import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import * as Icon from '@mui/icons-material/';
import { createBrowserHistory } from 'history';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BoxButtons, Button } from '../../../Styles/ScreenStyle';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import { Loader } from '../../../../shared/components/Loader';
import PapelController from '../../../../controllers/PapelController';
import { Papel } from '../../../../routes/RoutesNames';

interface TelaItem {
  tela_id: number;
  tela_chave: string;
  tela_nome: string;
  tela_grupo: string;
  marcada: string;
  /** Ações concedidas, separadas por vírgula. VAZIO = sem restrição = todas. */
  acoes?: string;
}

interface AcaoItem {
  acao_chave: string;
  acao_nome: string;
}

/**
 * Cadastro de papel: o nome e as telas que ele dá acesso.
 *
 * As telas aparecem agrupadas pelo mesmo grupo do menu (Cadastros, Financeiro, Vendas...),
 * porque é assim que quem configura pensa — "o financeiro vê o módulo financeiro" — e
 * procurar numa lista corrida de 31 itens é pior do que parece.
 */
export default function PapelCadastrar() {
  const navigate = useNavigate();
  const history = createBrowserHistory();
  const { id } = useParams();
  const roleId = id ? parseInt(id, 10) : null;

  const [nome, setNome] = useState('');
  const [telas, setTelas] = useState<TelaItem[]>([]);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [catalogoAcoes, setCatalogoAcoes] = useState<AcaoItem[]>([]);
  /* Por tela, as ações marcadas. CONJUNTO VAZIO = SEM RESTRIÇÃO = todas, e é por isso
     que a tela desenha as cinco caixas MARCADAS nesse caso: é o que o papel faz hoje. */
  const [acoesPorTela, setAcoesPorTela] = useState<Map<string, Set<string>>>(new Map());
  const [protegido, setProtegido] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erroNome, setErroNome] = useState('');

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const carregar = async () => {
    setLoading(true);

    if (roleId) {
      const dados = await PapelController.Listar('');
      if (Array.isArray(dados.Data)) {
        const papel = dados.Data.find((p: any) => p.RoleId === roleId);
        setNome(papel?.Nome ?? '');
        setProtegido(papel?.protegido === 'S');
      }

      const resposta = await PapelController.Telas(roleId);
      if (Array.isArray(resposta.Data)) {
        setTelas(resposta.Data);
        setMarcadas(
          new Set(
            resposta.Data.filter((t: TelaItem) => t.marcada === 'S').map(
              (t: TelaItem) => t.tela_chave
            )
          )
        );
        setAcoesPorTela(lerAcoes(resposta.Data));
      }
    } else {
      // Papel novo: o catálogo vem do papel 0, que não existe - então nenhuma marcada.
      const resposta = await PapelController.Telas(0);
      if (Array.isArray(resposta.Data)) {
        setTelas(resposta.Data);
        setMarcadas(new Set());
        setAcoesPorTela(new Map());
      }
    }

    const cat = await PapelController.Acoes();
    if (Array.isArray(cat.Data)) setCatalogoAcoes(cat.Data);

    setLoading(false);
  };

  /** O que veio do banco: vírgula separa, e vazio quer dizer todas. */
  const lerAcoes = (lista: TelaItem[]) => {
    const mapa = new Map<string, Set<string>>();
    lista.forEach((t) => {
      const marcadasDaTela = (t.acoes ?? '')
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean);
      mapa.set(t.tela_chave, new Set(marcadasDaTela));
    });
    return mapa;
  };

  /**
   * As ações a mostrar marcadas para uma tela.
   *
   * Conjunto vazio no estado significa SEM RESTRIÇÃO, e sem restrição o papel faz
   * tudo — então a tela mostra todas marcadas. Mostrar nenhuma seria mentir sobre o
   * que o papel pode fazer hoje, e o primeiro Salvar depois disso tiraria acesso que
   * ninguém pediu para tirar.
   */
  const acoesVisiveis = (chave: string) => {
    const atual = acoesPorTela.get(chave);
    if (!atual || atual.size === 0) return new Set(catalogoAcoes.map((a) => a.acao_chave));
    return atual;
  };

  const alternarAcao = (chave: string, acao: string) => {
    setAcoesPorTela((anterior) => {
      const novo = new Map(anterior);
      const atuais = new Set(acoesVisiveis(chave));
      if (atuais.has(acao)) atuais.delete(acao);
      else atuais.add(acao);
      novo.set(chave, atuais);
      return novo;
    });
  };

  const grupos = useMemo(() => {
    const mapa = new Map<string, TelaItem[]>();
    telas.forEach((t) => {
      const g = t.tela_grupo || 'Outros';
      if (!mapa.has(g)) mapa.set(g, []);
      mapa.get(g)!.push(t);
    });
    return Array.from(mapa.entries());
  }, [telas]);

  const alternar = (chave: string) => {
    setMarcadas((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  };

  const alternarGrupo = (itens: TelaItem[], marcar: boolean) => {
    setMarcadas((anterior) => {
      const novo = new Set(anterior);
      itens.forEach((t) => (marcar ? novo.add(t.tela_chave) : novo.delete(t.tela_chave)));
      return novo;
    });
  };

  const salvar = async () => {
    const limpo = nome.trim();

    if (limpo.length < 3) {
      setErroNome('Informe um nome com pelo menos 3 caracteres.');
      return;
    }
    setErroNome('');
    setLoading(true);

    const gravado = await PapelController.Gravar(roleId, limpo);

    if (gravado.StatusCode !== 200) {
      setLoading(false);
      toast(gravado?.Data ?? 'Não foi possível gravar o papel.', {
        type: 'error',
        autoClose: 10000,
      });
      return;
    }

    const idFinal = roleId ?? gravado?.Data?.RoleId ?? gravado?.Data?.roleId;

    // O Administrador tem todas as telas e a procedure recusa alterá-las. Não mando a
    // lista para não receber uma recusa que o usuário não provocou.
    if (idFinal && !protegido) {
      /* Só as telas MARCADAS entram, e a tela cujas ações estiverem todas ligadas vai
         com lista VAZIA — que no banco significa "sem restrição". É a mesma coisa na
         prática e deixa a tabela limpa: papel sem restrição nenhuma não ocupa linha. */
      const acoesParaGravar = Array.from(marcadas).map((chave) => {
        const visiveis = acoesVisiveis(chave);
        const todas = catalogoAcoes.length > 0 && visiveis.size === catalogoAcoes.length;
        return { tela: chave, acoes: todas ? [] : Array.from(visiveis) };
      });

      const telasGravadas = await PapelController.TelasGravar(
        idFinal,
        Array.from(marcadas),
        acoesParaGravar
      );

      if (telasGravadas.StatusCode !== 204) {
        setLoading(false);
        toast(telasGravadas?.Data ?? 'O papel foi gravado, mas as telas não.', {
          type: 'error',
          autoClose: 10000,
        });
        return;
      }
    }

    setLoading(false);
    toast(roleId ? 'Papel atualizado.' : 'Papel incluído.', { type: 'success' });
    navigate(Papel);
  };

  return (
    <Loader loading={loading}>
      <Box>
        <HeaderComponent
          text={roleId ? 'EDITAR PAPEL' : 'NOVO PAPEL'}
          className={'mb-5'}
        />

        <BoxButtons>
          <Button
            variant='contained'
            onClick={() => history.back()}
            color='warning'
            startIcon={<Icon.ArrowBack />}
          >
            Voltar
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={<Icon.Save />}
            onClick={salvar}
          >
            Salvar
          </Button>
        </BoxButtons>

        <Box className='p-3'>
          <TextField
            label='Nome do papel'
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={Boolean(erroNome)}
            helperText={erroNome}
            disabled={protegido}
            sx={{ width: '35ch', mb: 3 }}
          />

          {protegido && (
            <Alert severity='info' sx={{ mb: 3 }}>
              O papel <strong>Administrador</strong> tem todas as telas e não pode ser
              renomeado nem alterado. Ele é o que garante que sempre exista alguém capaz de
              configurar os acessos.
            </Alert>
          )}

          <Typography variant='h6' sx={{ mb: 1 }}>
            Telas deste papel
          </Typography>
          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            Quem tiver este papel enxerga no menu — e consegue abrir — apenas as telas
            marcadas. Um usuário com mais de um papel enxerga a soma de todos.
          </Typography>
          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            Em cada tela, as ações ao lado dizem o que o papel pode fazer lá dentro.
            Tela com <strong>todas</strong> as ações marcadas é o mesmo que tela sem
            restrição — é assim que todos os papéis começam. Desmarcar uma ação passa a
            recusar a operação no servidor, não só esconder o botão.
          </Typography>

          {grupos.map(([grupo, itens]) => {
            const todas = itens.every((t) => marcadas.has(t.tela_chave));

            return (
              <Box key={grupo} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    {grupo}
                  </Typography>
                  <Button
                    size='small'
                    variant='text'
                    disabled={protegido}
                    onClick={() => alternarGrupo(itens, !todas)}
                  >
                    {todas ? 'desmarcar todas' : 'marcar todas'}
                  </Button>
                </Box>

                <Box sx={{ pl: 1 }}>
                  {itens.map((t) => {
                    const ligada = marcadas.has(t.tela_chave);
                    const visiveis = acoesVisiveis(t.tela_chave);

                    return (
                      <Box
                        key={t.tela_chave}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <FormControlLabel
                          sx={{ minWidth: '18rem' }}
                          control={
                            <Checkbox
                              checked={ligada}
                              disabled={protegido}
                              onChange={() => alternar(t.tela_chave)}
                            />
                          }
                          label={t.tela_nome}
                        />

                        {/* As ações só fazem sentido com a tela ligada: sem a tela, o
                            papel não chega lá de jeito nenhum. */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', opacity: ligada ? 1 : 0.4 }}>
                          {catalogoAcoes.map((a) => (
                            <FormControlLabel
                              key={a.acao_chave}
                              sx={{ mr: 1 }}
                              control={
                                <Checkbox
                                  size='small'
                                  checked={visiveis.has(a.acao_chave)}
                                  disabled={protegido || !ligada}
                                  onChange={() => alternarAcao(t.tela_chave, a.acao_chave)}
                                />
                              }
                              label={<Typography variant='body2'>{a.acao_nome}</Typography>}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Loader>
  );
}
